import React, { useState } from 'react';
import { AlertTriangleIcon, ChevronDownIcon, ClockIcon, FileSearchIcon } from 'lucide-react';
import { loans, type Loan } from '../data/loanData';
import { followUpLeads } from '../data/followUps';
import { formatVolume } from '../data/pipeline';
type AttentionItem = {
  id: string;
  icon: React.ElementType;
  tone: 'danger' | 'warn' | 'brand';
  title: string;
  detail: string;
  action: string;
  loan?: Loan;
};
const toneClasses = {
  danger: 'bg-danger-soft text-danger',
  warn: 'bg-amber-100 text-amber-700',
  brand: 'bg-brand-soft text-brand-dark'
} as const;
type AttentionCardProps = {
  onOpenLoan: (loan: Loan) => void;
};
export function AttentionCard({
  onOpenLoan
}: AttentionCardProps) {
  const [collapsed, setCollapsed] = useState(false);
  const overdueFollowUps = followUpLeads.filter(lead => lead.status === 'pending' && new Date(lead.nextFollowUp).getTime() < Date.now());
  const items: AttentionItem[] = [...loans.filter(loan => loan.stalled).map(loan => ({
    id: `stalled-${loan.id}`,
    icon: AlertTriangleIcon,
    tone: 'warn' as const,
    title: `${loan.borrower} · ${loan.number}`,
    detail: `${loan.daysInStage} days in ${loan.stage} · ${formatVolume(loan.amount)}`,
    action: 'Unblock the file',
    loan
  })), ...loans.filter(loan => loan.conditions.some(condition => condition.status === 'AI Matched')).map(loan => ({
    id: `docs-${loan.id}`,
    icon: FileSearchIcon,
    tone: 'brand' as const,
    title: `${loan.borrower} · ${loan.number}`,
    detail: `${loan.conditions.filter(condition => condition.status === 'AI Matched').length} document(s) matched to conditions`,
    action: 'Review documents',
    loan
  })), ...overdueFollowUps.map(lead => ({
    id: `overdue-${lead.id}`,
    icon: ClockIcon,
    tone: 'danger' as const,
    title: lead.name,
    detail: 'Follow-up is past due',
    action: 'Call now'
  }))];
  return <section className="rounded-xl border border-gray-200 bg-white" aria-labelledby="attention-heading" data-id="element-57" data-mp="2oxxj344n6kbe">
      <div role="button" tabIndex={0} aria-expanded={!collapsed} aria-controls="attention-list" onClick={() => setCollapsed(prev => !prev)} onKeyDown={event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setCollapsed(prev => !prev);
      }
    }} className="flex cursor-pointer select-none items-center gap-3 border-b border-gray-200 px-5 py-3.5 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand/30" data-id="element-58" data-mp="2k5vwao9ulb74">
        <h2 id="attention-heading" className="text-[14px] font-semibold text-ink" data-id="element-59" data-mp="3vlkjngr0kc8g">
          At risk
        </h2>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700" data-id="element-60" data-mp="3qnsqykijom37">
          {items.length}
        </span>
        <p className="hidden text-[12px] text-gray-500 sm:block" data-id="element-61" data-mp="6zpj5yq647cc">
          files and follow-ups that will slip without you today
        </p>
        <ChevronDownIcon className={['ml-auto h-4 w-4 shrink-0 text-gray-400 transition-transform', collapsed ? '-rotate-90' : 'rotate-0'].join(' ')} aria-hidden="true" data-id="element-62" data-mp="brhcgh0pkr66" />
      </div>

      <ul id="attention-list" hidden={collapsed} className="divide-y divide-gray-100" data-id="element-63" data-mp="3k6hibjsymszt">
        {items.map(item => <li key={item.id} data-id="element-64" data-mp="3p5knd48xwz7s">
            <button type="button" onClick={() => item.loan && onOpenLoan(item.loan)} className="flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-gray-50" data-id="element-65" data-mp="3p49dcmowiv88">
              <span className={['flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', toneClasses[item.tone]].join(' ')} data-id="element-66" data-mp="19epvm5d8lo50">
                <item.icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-67" data-mp="2dd3crmml845p" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-68" data-mp="2rcuiz16v4g95">
                <span className="block truncate text-[13px] font-semibold text-ink" data-id="element-69" data-mp="2rdme6awjsbyw">
                  {item.title}
                </span>
                <span className="block truncate text-[12px] text-gray-500" data-id="element-70" data-mp="31a8xuwd50wzc">
                  {item.detail}
                </span>
              </span>
              <span className="shrink-0 text-[12px] font-medium text-brand" data-id="element-71" data-mp="2wawbecq6hrxa">
                {item.action}
              </span>
            </button>
          </li>)}
        {items.length === 0 && <li className="px-5 py-8 text-center text-[12.5px] text-gray-500" data-id="element-72" data-mp="3620qrdjrnesn">
            Nothing at risk right now.
          </li>}
      </ul>
    </section>;
}